import React, { useEffect, useRef, useState } from "react";
import { useAsyncEffect } from "tap/utils/hooks/useAsyncEffect";
import { useLoading } from "tap/utils/hooks/useLoading";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { Col, Radio as AntdRadio, Row } from "antd";
import Button from "@dtci/spellbook/Button";
import { MultiSelect } from "@dtci/spellbook/MultiSelect";
import Tooltip from "@dtci/spellbook/Tooltip";
import { InputLabel } from "tap/components/InputLabel";
import { ClearableFilterInput } from "tap/components/ClearableFilterInput";
import { useRateCardsActions } from "tap/store/rateCards/useRateCards.js";
import { useRateCardsData } from "../../hooks/useRateCardsData";
import { tapApi } from "tap/api.js";
import { parseSearchParams, updateSearchParams } from "tap/utils/urlParams.js";
import { getItemRange } from "tap/utils/pagination.js";
import { validateSpecialCharacters } from "tap/utils/validateSpecialCharacters.js";
import {
  buildFilterFromParams,
  cleanRateCardFilter,
} from "./utils/rateCardsSearchUtils";
import "./styles.scss";

/**
 * Search form for filtering the Rate Cards grid.
 *
 * Available filters:
 * - Rate Card ID: exact ID search
 * - Rate Card Name Keywords: comma-separated keywords for name search
 * - Keyword Match Type: radio to match ALL or ANY keywords
 * - TPR Display: radio to hide/show/only show TPRs
 * - Users: multi-select for assigned users
 * - Status: multi-select for rate card status
 *
 * Defaults: tprDisplay="hide", status="ACTIVE", keywordMatchType="all"
 * Filter state syncs bidirectionally with URL params for shareable searches.
 */
export function RateCardsSearchForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const isInitialMount = useRef(true);
  const { setLoadingTrue, setLoadingFalse } = useLoading();
  const {
    fetchRateCards,
    updateRateCards,
    rateCardsRange,
    rateCardsSort,
    filterForm,
    itemsPerPage,
  } = useRateCardsData();
  const { updateRateCardsSearchForm, clearRateCards } = useRateCardsActions();
  const { userOptions, statusOptions } = useSelector((s) => s.rateCards);

  const [validationErrors, setValidationErrors] = useState({
    rateCardId: undefined,
    rateCardNameKeywords: undefined,
  });
  const debounceTimerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useAsyncEffect(async ({ isMounted }) => {
    try {
      const userRes = await tapApi.users(
        { dataType: "rateCard" },
        [0, 199],
        ["fullName", "ASC"],
      );

      if (isMounted()) {
        const users =
          userRes?.content?.map(({ fullName, id }) => ({
            label: fullName,
            value: id,
          })) ?? [];
        updateRateCards({
          userOptions: users,
        });
      }
    } catch (error) {
      console.error("Failed to load initial options:", error);
      if (isMounted()) {
        updateRateCards({ userOptions: [] });
      }
    }
  }, []);

  // Sync URL params to form state on navigation (location.key changes on each navigation)
  // Pass null when no URL params to preserve Redux defaults (tprDisplay: hide, status: ACTIVE, keywordMatchType: all)
  useEffect(() => {
    const params = parseSearchParams(location.search);
    const filterData = buildFilterFromParams(
      params,
      userOptions,
      statusOptions,
    );
    let rangeOverride;
    if (filterData.pageSize) {
      rangeOverride = getItemRange(0, filterData.pageSize);
      updateRateCards({ itemsPerPage: filterData.pageSize });
    }

    const hasUrlParams = Object.keys(filterData).length > 0;

    if (hasUrlParams) {
      updateRateCardsSearchForm({ key: "filterForm", data: filterData });
    }

    // Merge URL-derived filters onto Redux filterForm so defaults (e.g. status ACTIVE)
    // apply when the URL only carries partial params such as pageSize.
    const filterForFetch =
      hasUrlParams ? { ...filterForm, ...filterData } : filterForm;

    fetchData(filterForFetch, rangeOverride);
    isInitialMount.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Re-parse URL params when options load (multi-select values depend on options being available)
  // Merges with filterForm to preserve defaults when rebuilding from URL
  useEffect(() => {
    if (isInitialMount.current) return;

    const params = parseSearchParams(location.search);
    const hasMultiSelectParams = params.users || params.status;

    if (
      hasMultiSelectParams &&
      (userOptions.length > 0 || statusOptions.length > 0)
    ) {
      const urlFilterData = buildFilterFromParams(
        params,
        userOptions,
        statusOptions,
      );
      const mergedFilterData = {
        ...filterForm,
        ...urlFilterData,
      };
      updateRateCardsSearchForm({ key: "filterForm", data: mergedFilterData });
      fetchData(mergedFilterData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOptions, statusOptions]);

  const fetchData = async (filterData, rangeOverride) => {
    setLoadingTrue();
    const range = rangeOverride ?? getItemRange(0, itemsPerPage);
    const data = await fetchRateCards(range, filterData);
    if (data) {
      updateRateCards({
        rateCards: data?.content,
        totalPages: data?.totalPages,
        totalItems: data?.totalElements,
        currentPage: data?.pageable?.pageNumber ?? 0,
        rateCardsRange: range,
      });
    }
    setLoadingFalse();
  };

  // Reset navigates to clean URL; the location.key useEffect handles re-fetch
  const handleResetFilter = () => {
    clearRateCards();
    setValidationErrors({
      rateCardId: undefined,
      rateCardNameKeywords: undefined,
    });
    navigate(location.pathname);
  };

  const handleInputChange = (field, value) => {
    let newValidationErrors = validationErrors;
    if (["rateCardId", "rateCardNameKeywords"].includes(field)) {
      const error = validateSpecialCharacters(value);
      newValidationErrors = { ...validationErrors, [field]: error };
      setValidationErrors(newValidationErrors);
    }

    const newFilterForm = { ...filterForm, [field]: value };
    updateRateCardsSearchForm({
      key: "filterForm",
      data: { [field]: value },
    });

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      const hasErrors = Object.values(newValidationErrors).some(
        (error) => error,
      );
      if (!hasErrors) {
        const filtered = cleanRateCardFilter(
          newFilterForm,
          getItemRange(0, itemsPerPage),
          rateCardsSort,
        );
        updateSearchParams(location, navigate, filtered);
      }
    }, 400);
  };

  const handleClearField = (field) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    const newValidationErrors = { ...validationErrors, [field]: undefined };
    setValidationErrors(newValidationErrors);

    const newFilterForm = { ...filterForm, [field]: "" };
    updateRateCardsSearchForm({
      key: "filterForm",
      data: { [field]: "" },
    });

    const resetRange = getItemRange(0, itemsPerPage);
    const filtered = cleanRateCardFilter(
      newFilterForm,
      resetRange,
      rateCardsSort,
    );
    updateSearchParams(location, navigate, filtered);
    fetchData(newFilterForm, resetRange);
  };

  return (
    <Row className="rate-cards-filter-form">
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Rate Card ID">
          <ClearableFilterInput
            value={filterForm?.rateCardId || ""}
            onChange={(e) => handleInputChange("rateCardId", e.target.value)}
            onClear={() => handleClearField("rateCardId")}
            width="auto"
            id="rateCardId"
            name="rateCardId"
            placeholder="Enter"
            className={validationErrors.rateCardId ? "input-error" : ""}
          />
          {validationErrors.rateCardId && (
            <div className="validation-error">
              {validationErrors.rateCardId}
            </div>
          )}
        </InputLabel>
      </Col>
      <Col>
        <Tooltip
          tippyProps={{
            placement: "bottom",
          }}
          tooltipContent="Please comma-separate multiple search keywords"
        >
          <InputLabel colProps={{ span: 24 }} label="Rate Card Name Keywords">
            <ClearableFilterInput
              value={filterForm?.rateCardNameKeywords || ""}
              onChange={(e) =>
                handleInputChange("rateCardNameKeywords", e.target.value)
              }
              onClear={() => handleClearField("rateCardNameKeywords")}
              width="auto"
              id="rateCardNameKeywords"
              name="rateCardNameKeywords"
              placeholder="Enter"
              className={
                validationErrors.rateCardNameKeywords ? "input-error" : ""
              }
            />
            {validationErrors.rateCardNameKeywords && (
              <div className="validation-error">
                {validationErrors.rateCardNameKeywords}
              </div>
            )}
          </InputLabel>
        </Tooltip>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Keyword Match Type">
          <AntdRadio.Group
            name="keywordMatchType"
            value={filterForm.keywordMatchType}
            onChange={(e) =>
              handleInputChange("keywordMatchType", e.target.value)
            }
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <AntdRadio value="all">
              Include <span style={{ fontWeight: "bold" }}>ALL</span> Keywords
            </AntdRadio>
            <AntdRadio value="any">
              Include <span style={{ fontWeight: "bold" }}>ANY</span> Keywords
            </AntdRadio>
          </AntdRadio.Group>
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="TPR Display">
          <AntdRadio.Group
            name="tprFlag"
            value={filterForm.tprDisplay}
            onChange={(e) => handleInputChange("tprDisplay", e.target.value)}
            style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}
          >
            <AntdRadio value="hide">Hide TPRs</AntdRadio>
            <AntdRadio value="include">Show TPRs</AntdRadio>
            <AntdRadio value="only">
              Show <span style={{ fontWeight: "bold" }}>ONLY</span> TPRs
            </AntdRadio>
          </AntdRadio.Group>
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Users">
          <MultiSelect
            onChange={(value) => handleInputChange("users", value)}
            name="users"
            options={userOptions}
            isSearchable
            isClearable
            value={filterForm.users || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <InputLabel colProps={{ span: 24 }} label="Status">
          <MultiSelect
            onChange={(value) => handleInputChange("status", value)}
            name="status"
            options={statusOptions}
            isSearchable
            isClearable
            value={filterForm.status || ""}
          />
        </InputLabel>
      </Col>
      <Col>
        <Button
          className="reset-button"
          sizeType="small"
          styleType="standard"
          onClick={handleResetFilter}
        >
          Reset
        </Button>
      </Col>
    </Row>
  );
}
